import React, { useState, useEffect, useCallback } from 'react';
import { X } from 'lucide-react';

// ─── Constants ────────────────────────────────────────────────────────────────

// Exported so other components (e.g. onboarding plan selector) can reuse
// the same translated content without duplicating strings.
export const TIER_NAMES = {
  en: {
    free_weekly:          'Free Weekly',
    free_monthly:         'Free Monthly',
    community_plus:       'Plus',
    enterprise_entry:     'Entry',
    enterprise_team:      'Team',
    enterprise_corporate: 'Corporate',
    enterprise_max:       'Max',
  },
  fr: {
    free_weekly:          'Gratuit Hebdomadaire',
    free_monthly:         'Gratuit Mensuel',
    community_plus:       'Plus',
    enterprise_entry:     'Entrée',
    enterprise_team:      'Équipe',
    enterprise_corporate: 'Entreprise',
    enterprise_max:       'Maximum',
  },
};

export const TIER_DESCRIPTIONS = {
  en: {
    free_weekly:          'Perfect for small groups with weekly events',
    free_monthly:         'Perfect for small groups with monthly events',
    community_plus:       'Great for multiple groups and families',
    enterprise_entry:     'For power users and active organizers',
    enterprise_team:      'For growing teams',
    enterprise_corporate: 'For larger organisations',
    enterprise_max:       'Unlimited calendars and events',
  },
  fr: {
    free_weekly:          'Idéal pour les petits groupes avec des événements hebdomadaires',
    free_monthly:         'Idéal pour les petits groupes avec des événements mensuels',
    community_plus:       'Parfait pour plusieurs groupes et les familles',
    enterprise_entry:     'Pour les utilisateurs avancés et les organisateurs actifs',
    enterprise_team:      'Pour les équipes en croissance',
    enterprise_corporate: 'Pour les grandes organisations',
    enterprise_max:       'Calendriers et événements illimités',
  },
};

export const MARKETING_BULLETS = {
  en: {
    free_weekly:          ['Share your calendar with unlimited subscribers via WhatsApp', 'Up to 5 events within any 31-day period — ideal for weekly meetings', 'No payment required — renews after 1 year'],
    free_monthly:         ['Share your calendar with unlimited subscribers via WhatsApp', 'Up to 12 events within any 12-month period — ideal for monthly meetings', 'No payment required — renews after 1 year'],
    community_plus:       ['Manage up to 3 separate calendars', 'Up to 10 events per calendar within any 31-day period', 'Renews after 1 year'],
    enterprise_entry:     ['Collaborative model — multiple calendar managers', '3 calendars, up to 20 events per calendar', 'Renews after 1 year'],
    enterprise_team:      ['Collaborative model — for growing teams', '5 calendars, up to 40 events per calendar', 'Renews after 1 year'],
    enterprise_corporate: ['Collaborative model — for larger organisations', '10 calendars, up to 100 events per calendar', 'Renews after 1 year'],
    enterprise_max:       ['Collaborative model — unlimited calendars and events', 'No limits on calendars or events', 'Renews after 1 year'],
  },
  fr: {
    free_weekly:          ['Partagez votre calendrier avec des abonnés illimités via WhatsApp', 'Jusqu\'à 5 événements sur toute période de 31 jours — idéal pour les réunions hebdomadaires', 'Aucun paiement requis — renouvellement après 1 an'],
    free_monthly:         ['Partagez votre calendrier avec des abonnés illimités via WhatsApp', 'Jusqu\'à 12 événements sur toute période de 12 mois — idéal pour les réunions mensuelles', 'Aucun paiement requis — renouvellement après 1 an'],
    community_plus:       ['Gérez jusqu\'à 3 calendriers distincts', 'Jusqu\'à 10 événements par calendrier sur toute période de 31 jours', 'Renouvellement après 1 an'],
    enterprise_entry:     ['Modèle collaboratif — plusieurs gestionnaires de calendrier', '3 calendriers, jusqu\'à 20 événements par calendrier', 'Renouvellement après 1 an'],
    enterprise_team:      ['Modèle collaboratif — pour les équipes en croissance', '5 calendriers, jusqu\'à 40 événements par calendrier', 'Renouvellement après 1 an'],
    enterprise_corporate: ['Modèle collaboratif — pour les grandes organisations', '10 calendriers, jusqu\'à 100 événements par calendrier', 'Renouvellement après 1 an'],
    enterprise_max:       ['Modèle collaboratif — calendriers et événements illimités', 'Aucune limite de calendriers ou d\'événements', 'Renouvellement après 1 an'],
  },
};

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  'https://whatsapp-calendar-backend-production.up.railway.app';

const SUPPORTED_CURRENCIES = ['GBP', 'EUR', 'USD'];

const CURRENCY_LABELS = {
  GBP: '£ GBP',
  EUR: '€ EUR',
  USD: '$ USD',
};

const CURRENCY_SYMBOLS = {
  GBP: '£',
  EUR: '€',
  USD: '$',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const detectBrowserLanguage = () => {
  const lang = (navigator.language || navigator.languages?.[0] || 'en').toLowerCase();
  return lang.startsWith('fr') ? 'fr' : 'en';
};

const formatPrice = (price, currency) => {
  if (price === null || price === undefined) return '—';
  const symbol = CURRENCY_SYMBOLS[currency] || currency;
  return `${symbol}${Number(price).toFixed(2)}`;
};

// ─── Guide Content ────────────────────────────────────────────────────────────

const buildContent = (lang, tiers, currency) => {
  if (lang === 'fr') {
    return {
      title: 'Guide de l\'utilisateur — TeleMatrix Consulting Calendar Hub',
      intro: (
        <>
          <p className="text-gray-700 mb-3">
            Calendar Hub fonctionne avec l'application de calendrier que vous utilisez déjà — Apple
            Calendrier, Google Agenda, Outlook, et autres. Il ne remplace pas votre application de
            calendrier ; il crée un lien d'abonnement qui permet à votre calendrier de recevoir des
            événements automatiquement.
          </p>
          <p className="text-gray-700 mb-3">
            Ce guide explique comment les différents utilisateurs peuvent s'abonner au TeleMatrix Consulting
            Calendar Hub et recevoir des mises à jour automatiques des événements selon leur appareil et
            leurs préférences d'application de calendrier.
          </p>
        </>
      ),
      sections: [
        {
          heading: 'Catégories d\'utilisateurs',
          content: (
            <div className="space-y-3">
              {[
                {
                  color: 'blue',
                  title: 'Catégorie 1 : iPhone + Apple Calendrier (Le plus simple et le plus rapide !)',
                  items: [
                    'Configuration : 30 secondes (2 appuis)',
                    'Vitesse de synchronisation : 5 à 15 minutes',
                    'Méthode : Lien webcal:// direct',
                    'FORTEMENT RECOMMANDÉ pour les utilisateurs d\'iPhone',
                  ],
                },
                {
                  color: 'gray',
                  title: 'Catégories 2 & 3 : iPhone/Android + Google Agenda',
                  items: [
                    'Configuration : 3 à 5 minutes',
                    'Vitesse de synchronisation : ~24 heures',
                    'Méthode : S\'abonner via navigateur mobile OU ordinateur',
                    'Fonctionne sur tous les appareils',
                  ],
                },
                {
                  color: 'gray',
                  title: 'Catégorie 4 : Android + Autres applications (Samsung, Outlook, etc.)',
                  items: [
                    'Configuration : 2 à 5 minutes (selon l\'application)',
                    'Vitesse de synchronisation : 1 à 4 heures (variable)',
                    'Méthode : Abonnement direct ou URL manuelle',
                  ],
                },
                {
                  color: 'gray',
                  title: 'Catégorie 5 : Ordinateur de bureau/portable (Mac/Windows)',
                  items: [
                    'Mac Apple Calendrier : ~1 heure de synchronisation',
                    'Google Agenda Web : ~24 heures de synchronisation',
                    'Windows Outlook : Variable',
                  ],
                },
                {
                  color: 'gray',
                  title: 'Catégorie 6 : Tablettes (iPad/Android)',
                  items: ['Même procédure que pour les téléphones correspondants ci-dessus'],
                },
              ].map(({ color, title, items }) => (
                <div
                  key={title}
                  className={`${
                    color === 'blue'
                      ? 'bg-blue-50 border-l-4 border-blue-500'
                      : 'bg-gray-50 border-l-4 border-gray-400'
                  } p-4 rounded`}
                >
                  <h5
                    className={`font-semibold ${
                      color === 'blue' ? 'text-blue-900' : 'text-gray-900'
                    }`}
                  >
                    {title}
                  </h5>
                  <ul className="mt-2 space-y-1 text-sm text-gray-700">
                    {items.map((item) => (
                      <li key={item}>• {item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ),
        },
        {
          heading: null,
          content: (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="text-lg font-semibold text-green-900 mb-3">
                ⚡ Configuration rapide : iPhone + Apple Calendrier (Recommandé !)
              </h4>
              <ol className="space-y-2 text-sm text-gray-700">
                {[
                  'Le propriétaire du calendrier partage le lien d\'abonnement via WhatsApp',
                  'Appuyez sur le lien sur votre iPhone',
                  'L\'iPhone ouvre automatiquement l\'application Calendrier',
                  'Une fenêtre contextuelle apparaît : « S\'abonner à [Nom du calendrier] ? »',
                  'Appuyez sur « S\'abonner »',
                  'C\'est fait ! Les événements apparaissent dans les 5 à 15 minutes',
                ].map((step, i) => (
                  <li key={i}>
                    <span className="font-semibold">{i + 1}.</span> {step}
                  </li>
                ))}
              </ol>
              <p className="mt-3 text-sm font-semibold text-green-800">
                ✓ Tous les événements futurs apparaissent automatiquement<br />
                ✓ Les mises à jour se synchronisent en 5 à 15 minutes<br />
                ✓ Le nom du calendrier s\'affiche correctement<br />
                ✓ Aucune autre action n\'est nécessaire
              </p>
            </div>
          ),
        },
        {
          heading: 'Configuration de Google Agenda (méthode via navigateur mobile)',
          content: (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <ol className="space-y-2 text-sm text-gray-700">
                {[
                  'Copiez l\'URL d\'abonnement depuis WhatsApp',
                  'Ouvrez Safari/Chrome (connectez-vous à votre compte Google si nécessaire)',
                  'Saisissez calendar.google.com dans la barre d\'adresse — n\'appuyez pas sur un lien, sinon l\'application de calendrier s\'ouvrira sur un téléphone Android et peut-être aussi sur un iPhone.',
                  'Vous serez peut-être redirigé directement vers la version bureau du calendrier (texte très petit) ou une version hybride avec une option « Bureau » en bas de l\'écran.',
                  'Si l\'application de calendrier continue de s\'ouvrir, déroulez le menu dans la barre d\'adresse (les trois points) et cochez « Site pour ordinateur » vers le bas du menu.',
                  'Pincez pour zoomer et rendre le texte plus lisible.',
                  'Dans la barre latérale gauche, appuyez sur « + » à côté de « Autres agendas »',
                  'Sélectionnez « À partir de l\'URL »',
                  'Collez l\'URL d\'abonnement copiée à l\'étape 1.',
                  'Appuyez sur « Ajouter un agenda »',
                  'IMPORTANT : Si nécessaire, actualisez la page du navigateur et le nom du calendrier devrait passer de l\'affichage de l\'URL au nom correct — ou finira par être synchronisé avec le nom correct.',
                ].map((step, i) => (
                  <li key={i}>
                    <span className={`font-semibold ${i === 10 ? 'text-red-600' : ''}`}>
                      {i + 1}.{i === 10 ? ' IMPORTANT :' : ''}
                    </span>{' '}
                    {i === 10 ? step.replace('IMPORTANT : ', '') : step}
                  </li>
                ))}
              </ol>
              <p className="mt-3 text-sm font-semibold text-gray-700">
                Délai de synchronisation : ~24 heures (limitation de Google)<br />
                Fonctionne sur tous les appareils avec le même compte Google
              </p>
            </div>
          ),
        },
        {
          heading: 'Fonctionnement des événements du calendrier',
          content: (
            <p className="text-sm text-gray-700">
              Le propriétaire du calendrier peut créer, supprimer et modifier des événements qui sont
              automatiquement synchronisés avec les utilisateurs abonnés au calendrier — aucune
              intervention de l\'utilisateur n\'est requise.
            </p>
          ),
        },
        {
          heading: 'Vitesse de synchronisation par application',
          content: (
            <div className="bg-gray-100 rounded-lg p-4">
              <ul className="space-y-2 text-sm text-gray-700">
                <li>• <span className="font-semibold">Apple Calendrier (iOS/Mac) :</span> 5 à 15 minutes ⚡ Le plus rapide !</li>
                <li>• <span className="font-semibold">Google Agenda :</span> ~24 heures</li>
                <li>• <span className="font-semibold">Outlook :</span> 4 à 24 heures (variable)</li>
                <li>• <span className="font-semibold">Samsung Calendrier :</span> 1 à 4 heures</li>
                <li>• <span className="font-semibold">Autres applications :</span> Variable</li>
              </ul>
            </div>
          ),
        },
        {
          heading: 'Forcer une actualisation manuelle (si nécessaire)',
          content: (
            <div className="space-y-2 text-sm text-gray-700">
              <p><span className="font-semibold">Apple Calendrier (iOS) :</span> Ouvrez l\'application → Faites glisser vers le bas pour actualiser</p>
              <p><span className="font-semibold">Google Agenda :</span> Ordinateur uniquement — Paramètres → Trouver le calendrier → Actualiser (peut ne pas fonctionner immédiatement en raison de la mise en cache de Google)</p>
            </div>
          ),
        },
      ],
      needHelp: 'Besoin d\'aide ? Contactez le propriétaire de votre calendrier pour obtenir de l\'assistance.',
      closeBtn: 'Fermer',
    };
  }

  // ── English ──────────────────────────────────────────────────────────────────
  return {
    title: 'User Guide — TeleMatrix Consulting Calendar Hub',
    intro: (
      <>
        <p className="text-gray-700 mb-3">
          Calendar Hub works with the calendar app you already use — Apple Calendar, Google Calendar,
          Outlook, and others. It doesn't replace your calendar app; instead, it creates a subscription
          link that enables your calendar to receive events automatically.
        </p>
        <p className="text-gray-700 mb-3">
          This guide explains how different users may subscribe to the TeleMatrix Consulting Calendar Hub
          and receive automatic event updates based on their device and calendar app preferences.
        </p>
      </>
    ),
    sections: [
      {
        heading: 'User Categories',
        content: (
          <div className="space-y-3">
            {[
              {
                color: 'blue',
                title: 'Category 1: iPhone + Apple Calendar (Easiest & Fastest!)',
                items: [
                  'Setup: 30 seconds (2 taps)',
                  'Sync Speed: 5–15 minutes',
                  'Method: Direct webcal:// link',
                  'HIGHLY RECOMMENDED for iPhone users',
                ],
              },
              {
                color: 'gray',
                title: 'Category 2 & 3: iPhone/Android + Google Calendar',
                items: [
                  'Setup: 3–5 minutes',
                  'Sync Speed: ~24 hours',
                  'Method: Subscribe via mobile browser OR desktop',
                  'Works across all devices',
                ],
              },
              {
                color: 'gray',
                title: 'Category 4: Android + Other Apps (Samsung, Outlook, etc.)',
                items: [
                  'Setup: 2–5 minutes (varies by app)',
                  'Sync Speed: 1–4 hours (varies)',
                  'Method: Direct subscription or manual URL',
                ],
              },
              {
                color: 'gray',
                title: 'Category 5: Desktop/Laptop (Mac/Windows)',
                items: [
                  'Mac Apple Calendar: ~1 hour sync',
                  'Google Calendar Web: ~24 hours sync',
                  'Windows Outlook: Varies',
                ],
              },
              {
                color: 'gray',
                title: 'Category 6: Tablets (iPad/Android)',
                items: ['Same process as phone equivalents above'],
              },
            ].map(({ color, title, items }) => (
              <div
                key={title}
                className={`${
                  color === 'blue'
                    ? 'bg-blue-50 border-l-4 border-blue-500'
                    : 'bg-gray-50 border-l-4 border-gray-400'
                } p-4 rounded`}
              >
                <h5
                  className={`font-semibold ${
                    color === 'blue' ? 'text-blue-900' : 'text-gray-900'
                  }`}
                >
                  {title}
                </h5>
                <ul className="mt-2 space-y-1 text-sm text-gray-700">
                  {items.map((item) => (
                    <li key={item}>• {item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        ),
      },
      {
        heading: null,
        content: (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h4 className="text-lg font-semibold text-green-900 mb-3">
              ⚡ Quick Setup: iPhone + Apple Calendar (Recommended!)
            </h4>
            <ol className="space-y-2 text-sm text-gray-700">
              {[
                'Calendar owner shares subscription link via WhatsApp',
                'Tap link on iPhone',
                'iPhone opens Calendar app automatically',
                'Popup: "Subscribe to [Calendar Name]?"',
                'Tap "Subscribe"',
                'Done! Events appear within 5–15 minutes',
              ].map((step, i) => (
                <li key={i}>
                  <span className="font-semibold">{i + 1}.</span> {step}
                </li>
              ))}
            </ol>
            <p className="mt-3 text-sm font-semibold text-green-800">
              ✓ All future events appear automatically<br />
              ✓ Updates sync within 5–15 minutes<br />
              ✓ Calendar name appears correctly<br />
              ✓ No further action needed
            </p>
          </div>
        ),
      },
      {
        heading: 'Google Calendar Setup (Mobile Browser Method)',
        content: (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <ol className="space-y-2 text-sm text-gray-700">
              {[
                'Copy subscription URL from WhatsApp',
                'Open Safari/Chrome (sign in to Google account if necessary)',
                'Type calendar.google.com into the address bar – don\'t tap a link otherwise the calendar app will open on an Android phone and perhaps also on an iPhone',
                'You may be taken directly to the desktop version (very small text) or a hybrid version with an option to select "Desktop" at the bottom',
                'If the calendar app continues to open, pull down the menu in the address bar (the three dots) and tick Desktop site towards the bottom of the menu',
                'Pinch to zoom in and make the text more readable',
                'In the left sidebar, tap the + next to "Other calendars"',
                'Select From URL',
                'Paste subscription URL copied in step 1',
                'Tap Add calendar',
                'IMPORTANT: If necessary, refresh the browser page and the calendar name should change from showing the URL to the correct name — or will eventually be synced to the correct name',
              ].map((step, i) => (
                <li key={i}>
                  <span className={`font-semibold ${i === 10 ? 'text-red-600' : ''}`}>
                    {i + 1}.
                  </span>{' '}
                  {step}
                </li>
              ))}
            </ol>
            <p className="mt-3 text-sm font-semibold text-gray-700">
              Sync time: ~24 hours (Google limitation)<br />
              Works on all devices with the same Google account
            </p>
          </div>
        ),
      },
      {
        heading: 'How Calendar Events Work',
        content: (
          <p className="text-sm text-gray-700">
            The calendar owner can create, delete and edit events that are automatically synced to users
            who have subscribed to the calendar — no user intervention is required.
          </p>
        ),
      },
      {
        heading: 'Sync Speed by App',
        content: (
          <div className="bg-gray-100 rounded-lg p-4">
            <ul className="space-y-2 text-sm text-gray-700">
              <li>• <span className="font-semibold">Apple Calendar (iOS/Mac):</span> 5–15 minutes ⚡ Fastest!</li>
              <li>• <span className="font-semibold">Google Calendar:</span> ~24 hours</li>
              <li>• <span className="font-semibold">Outlook:</span> 4–24 hours (varies)</li>
              <li>• <span className="font-semibold">Samsung Calendar:</span> 1–4 hours</li>
              <li>• <span className="font-semibold">Other apps:</span> Varies</li>
            </ul>
          </div>
        ),
      },
      {
        heading: 'Force Manual Refresh (if needed)',
        content: (
          <div className="space-y-2 text-sm text-gray-700">
            <p><span className="font-semibold">Apple Calendar (iOS):</span> Open app → Pull down to refresh</p>
            <p><span className="font-semibold">Google Calendar:</span> Desktop only — Settings → Find calendar → Refresh (may not work immediately due to Google's caching)</p>
          </div>
        ),
      },
    ],
    needHelp: 'Need help? Contact your calendar owner for support.',
    closeBtn: 'Close',
  };
};

// Interim default guide (see note inside UserGuideModal).
const INTERIM_DEFAULT_GUIDE = { name: 'Hot Club', url: '/guides/HotClub-meeting-deck-E-08.pdf' };

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * UserGuideModal
 *
 * Props:
 *   isOpen        {boolean}   — whether the modal is visible
 *   onClose       {function}  — called when user dismisses the modal
 *   isOnDashboard {boolean}   — when true, reads preferred currency from user profile;
 *                               when false (Welcome screen), always defaults to GBP
 *   userPreferredCurrency {string|null} — currency code from user profile (dashboard only)
 *   showPricing   {boolean}   — (26 Sep 2026) Subscriptions & Pricing and the currency
 *                               selector. Default now FALSE everywhere: the tier/pricing
 *                               content is obsolete (no plans exist). Kept, not deleted,
 *                               pending the full guide rewrite.
 *   calendarGuide {{name, url}|null} — (26 Sep 2026, H1) the current calendar's own guide
 *                               (calendars.guide_url), shown first with Open / Download
 */
const UserGuideModal = ({ isOpen, onClose, isOnDashboard = false, userPreferredCurrency = null, showPricing = false, calendarGuide = null }) => {
  // (26 Sep 2026) INTERIM, Neil's call: every Guide button — sign-in panel,
  // onboarding, dashboard, member screen — shows the Hot Club guide unless the
  // calendar has its own guide_url. Fine while there are no Apéro users; replace
  // with the revised general guide (E-07 PDF) before the first Apéro user joins.
  // Keep this URL in step with Hot Club's calendars.guide_url.
  const guide = calendarGuide?.url ? calendarGuide : INTERIM_DEFAULT_GUIDE;
  const [lang, setLang] = useState('en');
  const [currency, setCurrency] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [loadingTiers, setLoadingTiers] = useState(false);

  // On open: set language from browser; set currency from context
  useEffect(() => {
    if (!isOpen) return;
    // (26 Sep 2026) A calendar's own guide is English-only — no EN/FR switch.
    setLang(guide?.url ? 'en' : detectBrowserLanguage());

    if (isOnDashboard && userPreferredCurrency && SUPPORTED_CURRENCIES.includes(userPreferredCurrency)) {
      setCurrency(userPreferredCurrency);
    } else {
      setCurrency('GBP');
    }
  }, [isOpen, isOnDashboard, userPreferredCurrency, guide?.url]);

  // Fetch tier pricing whenever currency changes (and modal is open)
  // On Welcome screen fetch all tiers (we split community/enterprise in the UI);
  // user_type is returned per tier so we can filter client-side
  const fetchTiers = useCallback(async (cur) => {
    setLoadingTiers(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/tiers?currency=${cur}`, { cache: 'no-store' });
      if (res.ok) {
        const data = await res.json();
        setTiers(data.tiers || []);
      }
    } catch (err) {
      console.error('Failed to fetch tiers:', err);
    } finally {
      setLoadingTiers(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen || !currency || !showPricing) return;
    fetchTiers(currency);
  }, [isOpen, currency, fetchTiers, showPricing]);

  // Prevent body scroll while modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  if (!isOpen) return null;

  const content = buildContent(lang, tiers, currency);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50"
      style={{ padding: 'env(safe-area-inset-top, 16px) 16px env(safe-area-inset-bottom, 16px) 16px' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="bg-white rounded-xl max-w-4xl w-full flex flex-col"
        style={{ maxHeight: 'min(90dvh, calc(100vh - env(safe-area-inset-top, 16px) - env(safe-area-inset-bottom, 16px) - 32px))' }}
      >

        {/* ── Header ── */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b gap-3">
          {/* Title */}
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 flex-1 min-w-0 truncate">
            {lang === 'fr' ? 'Guide de l\'utilisateur' : 'User Guide'}
          </h2>

          {/* Controls: language + currency + close */}
          <div className="flex items-center gap-2 shrink-0">

            {/* Language selector — hidden for a calendar's own (English-only) guide */}
            {!guide?.url && (
            <div className="flex rounded-lg border border-gray-200 overflow-hidden text-sm">
              <button
                onClick={() => setLang('en')}
                title="English"
                className={`px-2 py-1 flex items-center transition-colors ${
                  lang === 'en'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                EN
              </button>
              <button
                onClick={() => setLang('fr')}
                title="Français"
                className={`px-2 py-1 flex items-center transition-colors border-l border-gray-200 ${
                  lang === 'fr'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                FR
              </button>
            </div>
            )}

            {/* Currency selector */}
            {showPricing && (
            <select
              value={currency || 'GBP'}
              onChange={(e) => setCurrency(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-2 py-1 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              title="Currency"
            >
              {SUPPORTED_CURRENCIES.map((c) => (
                <option key={c} value={c}>
                  {CURRENCY_LABELS[c]}
                </option>
              ))}
            </select>
            )}

            {/* Close button */}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-1 rounded"
              aria-label="Close"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* ── Scrollable Content ── */}
        <div className="overflow-y-auto p-4 sm:p-6 space-y-6">
          {/* (26 Sep 2026, H1) This calendar's own guide. Opened in a new tab
              rather than embedded: iPhone Safari shows only page 1 of a PDF
              inside a frame. */}
          {guide?.url && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                {`${guide.name} guide`}
              </h3>
              {/* One button: the PDF viewer (desktop or iPhone) has its own
                  save/share, so a separate Download did the same thing. */}
              <a
                href={guide.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition"
              >
                Open guide
              </a>
              <p className="text-sm text-gray-600 mt-3">
                Opens as a PDF. To return to Calendar Hub, close the guide or use your browser's back arrow.
              </p>
            </div>
          )}

          {/* (26 Sep 2026) When the calendar has its own guide, show only that
              card and "Need help?" — the general guide below is out of date. */}
          {!guide?.url && (<>
          {/* Intro */}
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">{content.title}</h3>
            {content.intro}
          </div>

        {/* ── Subscriptions & Pricing ── */}
          {showPricing && (
          <div>
            <h4 className="text-lg font-semibold text-gray-900 mb-3">
              {lang === 'fr' ? 'Abonnements et tarifs' : 'Subscriptions & Pricing'}
            </h4>

            {loadingTiers ? (
              <p className="text-sm text-gray-500 italic">
                {lang === 'fr' ? 'Chargement des tarifs…' : 'Loading pricing…'}
              </p>
            ) : (() => {
              // ── Shared helpers ────────────────────────────────────────────
              const tierNames = TIER_NAMES[lang];
              const tierDescriptions = TIER_DESCRIPTIONS[lang];
              const marketingBullets = MARKETING_BULLETS[lang];

              const renderTierCard = (t) => {
                const bullets = marketingBullets[t.tier_name] || [];
                return (
                  <div key={t.id} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <div className="flex justify-between items-start">
                      <h5 className="font-semibold text-gray-900">
                        {tierNames[t.tier_name] || t.display_name}
                      </h5>
                      <span className="text-sm font-semibold text-blue-700">
                        {t.price === 0 || t.price === null
                          ? (lang === 'fr' ? 'Gratuit' : 'Free')
                          : `${formatPrice(t.price, currency)}/${lang === 'fr' ? 'an' : 'year'}`}
                      </span>
                    </div>
                    {(tierDescriptions[t.tier_name] ?? t.description) && (
                      <p className="text-sm text-gray-600 mt-1 italic">
                        {tierDescriptions[t.tier_name] ?? t.description}
                      </p>
                    )}
                    {bullets.length > 0 && (
                      <ul className="mt-2 space-y-1">
                        {bullets.map((b, i) => (
                          <li key={i} className="text-sm text-gray-700 flex items-start gap-1">
                            <span className="text-green-600 font-bold shrink-0">✓</span>
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              };

              const communityTiers = tiers.filter(t => t.user_type === 'community');
              const enterpriseTiers = tiers.filter(t => t.user_type === 'enterprise');

              return (
                <div className="space-y-4">
                  {/* ── Community Level ── */}
                  {communityTiers.length > 0 && (
                    <div>
                      <h5 className="text-sm font-bold text-blue-700 uppercase tracking-wide mb-2">
                        {lang === 'fr' ? 'Niveau Communauté' : 'Community Level'}
                      </h5>
                      <div className="space-y-2">
                        {communityTiers.map(renderTierCard)}
                      </div>
                    </div>
                  )}

                  {/* ── Enterprise Level ── */}
                  {isOnDashboard ? (
                    enterpriseTiers.length > 0 && (
                      <div>
                        <h5 className="text-sm font-bold text-purple-700 uppercase tracking-wide mb-2">
                          {lang === 'fr' ? 'Niveau Entreprise' : 'Enterprise Level'}
                        </h5>
                        <div className="space-y-2">
                          {enterpriseTiers.map(renderTierCard)}
                        </div>
                      </div>
                    )
                  ) : (
                    <details className="group">
                      <summary className="cursor-pointer text-sm text-purple-700 font-medium list-none flex items-center gap-1 select-none">
                        <span className="group-open:rotate-90 transition-transform inline-block">▶</span>
                        {lang === 'fr'
                          ? 'Aussi disponible pour les entreprises — voir les offres Entreprise'
                          : 'Also available for business use — see Enterprise plans'}
                      </summary>
                      <div className="mt-2 space-y-2">
                        {enterpriseTiers.length > 0 ? (
                          enterpriseTiers.map(renderTierCard)
                        ) : (
                          <p className="text-sm text-gray-500 italic pl-4">
                            {lang === 'fr'
                              ? 'Contactez-nous via telematrixconsulting.com pour en savoir plus.'
                              : 'Contact us via telematrixconsulting.com to find out more.'}
                          </p>
                        )}
                      </div>
                    </details>
                  )}

                  {/* Dashboard nudge — Welcome screen only */}
                  {!isOnDashboard && (
                    <p className="text-xs text-gray-900 font-medium mt-1">
                      {lang === 'fr'
                        ? 'Pour le détail complet des règles et limites, consultez le Guide depuis votre tableau de bord après vous être connecté.'
                        : 'For full pricing rules and limits, open the Guide from your dashboard after signing in.'}
                    </p>
                  )}
                </div>
              );
            })()}
          </div>
          )}
              
          {/* ── How-to sections ── */}
          {content.sections.map((section, idx) => (
            <div key={idx}>
              {section.heading && (
                <h4 className="text-lg font-semibold text-gray-900 mb-3">{section.heading}</h4>
              )}
              {section.content}
            </div>
          ))}
          </>)}

          {/* Need help */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
            <p className="text-sm text-gray-700">
              <span className="font-semibold">
                {lang === 'fr' ? 'Besoin d\'aide ?' : 'Need help?'}
              </span>{' '}
              {lang === 'fr' ? (
                <>
                  Contactez l'administrateur de votre calendrier pour obtenir de l'assistance, ou rendez-vous sur{' '}
                  <a
                    href="https://telematrixconsulting.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 underline"
                  >
                    telematrixconsulting.com
                  </a>
                  {' '}pour nous envoyer un e-mail.
                </>
              ) : (
                <>
                  Contact your administrator for support, or go to{' '}
                  <a
                    href="https://telematrixconsulting.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 underline"
                  >
                    telematrixconsulting.com
                  </a>
                  {' '}and send us an email.
                </>
              )}
            </p>
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="border-t p-4 text-center">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            {content.closeBtn}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserGuideModal;
